import { useCallback, useState } from 'react';
import api from '../../utils/api';
import { handleBlobDownloadResponse } from '../../utils/blobDownload';
import { bulkDownloadIdCards } from '../../utils/idCardExportService';
import { idCardImageCacheKey } from '../../utils/idCardCardData';

const CHUNK_SIZE = 20;
const SYNC_MAX = 500;

export function useBulkDownload(documentType) {
  const [progress, setProgress] = useState({ done: 0, total: 0, label: '' });
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const runSyncBulk = useCallback(async ({
    studentIds,
    options,
    output,
    classId,
    sectionId,
    schoolId,
    onProgress,
  }) => {
    let token = '';
    let done = 0;
    const total = studentIds.length;
    setProgress({ done: 0, total, label: 'Generating…' });

    for (let i = 0; i < studentIds.length; i += CHUNK_SIZE) {
      const chunk = studentIds.slice(i, i + CHUNK_SIZE);
      const res = await api.post(`/reports/${documentType}/bulk/chunk`, {
        student_ids: chunk,
        token: token || undefined,
        class_id: classId,
        section_id: sectionId,
        school_id: schoolId,
        ...options,
      });
      token = res.data?.data?.token || token;
      done += chunk.length;
      setProgress({ done, total, label: 'Generating…' });
      onProgress?.({ done, total });
    }

    setProgress({ done: total, total, label: 'Assembling…' });
    const finalizeRes = await api.post(
      `/reports/${documentType}/bulk/finalize`,
      {
        token,
        output,
        class_id: classId,
        section_id: sectionId,
        school_id: schoolId,
        ...options,
      },
      { responseType: 'blob' },
    );

    const kind = output === 'merged' ? 'pdf' : 'zip';
    return handleBlobDownloadResponse(
      finalizeRes,
      kind,
      `reports-${Date.now()}.${kind}`,
    );
  }, [documentType]);

  const runIdCardBulk = useCallback(async ({
    studentIds,
    students,
    school,
    options,
    printSettings,
    output,
    onProgress,
  }) => {
    const selected = students.filter((s) => studentIds.includes(s.id));
    if (!selected.length) throw new Error('No students selected');
    if (!school) throw new Error('School ID card settings not loaded');

    const format = options.card_format || options.cardFormat || 'portrait';
    const design = options.card_design || options.cardDesign || 'saffron';
    const cacheKey = idCardImageCacheKey(school);

    return bulkDownloadIdCards({
      students: selected,
      school,
      format,
      design,
      imageCacheKey: cacheKey,
      output,
      printSettings: printSettings || {},
      onProgress: (p) => {
        setProgress(p);
        onProgress?.(p);
      },
    });
  }, []);

  const runBulk = useCallback(async ({
    studentIds,
    students = [],
    school = null,
    options = {},
    printSettings = undefined,
    output = 'zip',
    classId,
    sectionId,
    schoolId,
    onProgress,
  }) => {
    if (!studentIds?.length) {
      throw new Error('No students selected');
    }

    if (studentIds.length > SYNC_MAX) {
      throw new Error(`Maximum ${SYNC_MAX} students per bulk operation`);
    }

    setRunning(true);
    setError('');

    try {
      if (documentType === 'id-card') {
        return await runIdCardBulk({
          studentIds,
          students,
          school,
          options,
          printSettings,
          output,
          onProgress,
        });
      }

      return await runSyncBulk({
        studentIds,
        options,
        output,
        classId,
        sectionId,
        schoolId,
        onProgress,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Bulk download failed';
      setError(msg);
      throw new Error(msg);
    } finally {
      setRunning(false);
      setProgress({ done: 0, total: 0, label: '' });
    }
  }, [documentType, runIdCardBulk, runSyncBulk]);

  return { runBulk, progress, running, error, setError };
}
